import React from 'react';

import { Box, Button, CenteredLayout, IconButton, WorkflowHeader } from '@betterment/samba-react';
import { Helmet } from 'react-helmet';

import { SignatureColorModeAware } from './SignatureColorModeAware';

type RootProps = {
  pageTitle: string | null;
};
const Root: React.FC<React.PropsWithChildren<RootProps>> = ({ children, pageTitle }) => (
  <Box
    css={{
      bg: 'background.surface',
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr',
      gridTemplateRows: 'auto auto 1fr auto',
      gridTemplateAreas: `
          "notifications notifications notifications"
          "header header header"
          ". content ."
          ". footer ."
        `,
      gridColumnGap: {
        base: 'space.0',
        md: 'space.400',
      },
      height: '100vh',
      overflow: 'auto',
    }}
    data-js-target="main"
  >
    <Helmet titleTemplate="%s | Betterment" defaultTitle="Betterment">
      <title>{pageTitle}</title>
    </Helmet>
    {children}
  </Box>
);

const Banner: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    css={{
      gridArea: 'notifications',
      display: 'grid',
      gridAutoRows: 'min-content',
      gridTemplateColumns: 'auto minmax(min-content, 1200px) auto',
      sm: { gridColumnGap: 'space.0' },
      md: { gridColumnGap: 'space.400' },
      rowGap: {
        base: 'space.300',
        mdDown: 'space.100',
      },
    }}
  >
    {children}
  </Box>
);

type HeaderProps = {
  workflowTitle: string | null;
  onClose: () => void;
  onBack?: () => void;
  onLogoClick?: () => void;
};
const Header: React.FC<HeaderProps> = ({ workflowTitle, onBack, onClose, onLogoClick }) => {
  const renderLogo = () => {
    if (onBack) {
      return <IconButton label="Back" name="LeftArrow" onClick={onBack} spacing="compact" />;
    }

    if (onLogoClick) {
      // using 6px for padding instead of a token to align with padding values from IconButton compact, which is also hard coded to 6px.
      return (
        <Button onClick={onLogoClick} variant="ghostSecondary" css={{ px: '6px', py: '6px', minHeight: 'unset' }}>
          <SignatureColorModeAware />
        </Button>
      );
    }

    return <SignatureColorModeAware />;
  };
  return (
    <Box
      css={{
        bg: 'background.surface',
        gridArea: 'header',
        marginBottom: {
          base: 'space.200',
          md: 'space.400',
        },
        paddingX: 'space.400',
        borderBottom: '1px solid',
        borderBottomColor: 'border.weak',
        position: 'sticky',
        top: '0',
        zIndex: 'compat-loader',
      }}
    >
      <WorkflowHeader
        css={{
          gridColumn: 2,
          px: 'space.0',
          maxWidth: '1200px',
          marginX: 'auto',
        }}
      >
        <WorkflowHeader.Leading css={{ display: 'flex', alignItems: 'center', gap: 'space.100' }}>
          {renderLogo()}
        </WorkflowHeader.Leading>
        <WorkflowHeader.Title>{workflowTitle}</WorkflowHeader.Title>
        <WorkflowHeader.Trailing>
          <IconButton label="Close" name="X" onClick={onClose} spacing="compact" />
        </WorkflowHeader.Trailing>
      </WorkflowHeader>
    </Box>
  );
};

type ContentProps = {
  size?: 'small' | 'medium' | 'large' | 'full';
};
const Content: React.FC<React.PropsWithChildren<ContentProps>> = ({ children, size = 'medium' }) => (
  <CenteredLayout
    css={{
      bg: 'background.surface',
      gridArea: 'content',
      marginBottom: {
        base: 'space.200',
        md: 'space.400',
      },
      paddingX: {
        base: 'space.400',
        smDown: 'space.300',
      },
      alignItems: 'unset',
    }}
    size={size}
  >
    {children}
  </CenteredLayout>
);

const Footer: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    css={{
      gridArea: 'footer',
      paddingX: {
        base: 'space.400',
        smDown: 'space.300',
      },
      maxWidth: '1200px',
    }}
  >
    {children}
  </Box>
);

export const WorkflowLayout = Object.assign(Root, {
  Banner,
  Header,
  Content,
  Footer,
});
